import { Group, Button } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useProposal } from '../../context/ProposalContext';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Proposal } from '../../../../interfaces/types';
import { notifications } from '@mantine/notifications';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { saveProposal } from '../../../../services/proposalsRoutes';
import { debounce } from 'lodash';

export default function SaveProposal() {
  const { proposal } = useProposal();
  const { getAccessTokenSilently, user } = useAuth0();
  const location = useLocation();
  const state = (location.state || {}) as { proposalId?: string; isNew?: boolean };
  const searchParams = new URLSearchParams(location.search);
  const proposalId = state.proposalId || searchParams.get('proposalId') || '';
  const isNew = state.isNew;
  const { t } = useTranslation();
  // Handle Save
  const handleActiveSave = useCallback(() => {
    if (!proposalId && !proposal.id) return; // guard
    const proposalToSave: Proposal = {
      ...proposal,
      id: proposal.id || proposalId,
      last_edited: new Date(),
      user_metadata: user?.user_metadata,
    }; 

    const saveProposalData = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        await saveProposal(accessToken, proposalToSave);
        notifications.show({
          title: t('saveSuccess'),
          message: t('proposalSaved'),
          color: 'green',
          icon: <IconCheck size={18} />,
          autoClose: 1500,
          position: 'top-right',
          withCloseButton: false,
        });
      } catch (error) {
        console.error('Error saving proposal:', error);
        notifications.show({
          title: t('saveError'),
          message: t('failedToSaveProposal'),
          color: 'red',
          autoClose: 3000,
          position: 'top-right',
        });
      }
    };
    saveProposalData();
  }, [proposal, getAccessTokenSilently, proposalId, user?.user_metadata, t]);

  const handlePassiveSave = useCallback(() => {
    if (!proposalId && !proposal.id) return; // guard
    const proposalToSave: Proposal = {
      ...proposal,
      id: proposal.id || proposalId,
      last_edited: new Date(),
      user_metadata: user?.user_metadata,
    };
    const saveProposalData = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        await saveProposal(accessToken, proposalToSave);
      } catch (error) {
        console.error('Error saving proposal:', error);
      }
    };
    saveProposalData();
  }, [proposal, proposalId, getAccessTokenSilently, user?.user_metadata]);

  // Saves proposal after 5 seconds have elapsed since editing
  const debouncedSave = useCallback(debounce(handlePassiveSave, 5000), [handlePassiveSave]);

  useEffect(() => {
    debouncedSave();
  }, [proposal]);

  useEffect(() => {
    // Handle save on page unload
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      handleActiveSave();
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      debouncedSave.cancel();
    };
  }, [handleActiveSave, debouncedSave]);

  return (
    <Button
      color="green"
      variant="light"
      size="md"
      leftSection={<IconCheck size={18} />}
      style={{ marginRight: '0.5rem' }}
      onClick={handleActiveSave}
    >
      {t('save')}
    </Button>
  );
}
